import { DEFAULT_CONFIG } from '../engine/config.ts';
import type { LocalStore } from './db.ts';
export const PLAN_LISTS = ['picks', 'ratings', 'notes', 'mustSee', 'profiles',
  'constraints', 'members', 'ledger', 'votes'] as const;

export function planFingerprint(value: unknown): string {
  const stable = (item: unknown): unknown => {
    if (Array.isArray(item)) return item.map(stable);
    if (item && typeof item === 'object') return Object.fromEntries(
      Object.entries(item).filter(([, v]) => v !== undefined).sort(([a], [b]) => a.localeCompare(b))
        .map(([key, v]) => [key, stable(v)]),
    );
    return item;
  };
  return JSON.stringify(stable(value));
}

// Normalize only representation and save bookkeeping; retain every authored field.
export function effectivePlanFingerprint(payload: Record<string, unknown>): string {
  const value = { ...payload };
  for (const field of ['savedAt', 'updatedAt', 'bytes']) delete value[field];
  for (const key of PLAN_LISTS) {
    value[key] = ((payload[key] ?? []) as Record<string, unknown>[]).map((row) => {
      const normalized = { ...row };
      if (key === 'picks') delete normalized.addedAt;
      if (key === 'ratings') delete normalized.updatedAt;
      return normalized;
    }).sort((a, b) => planFingerprint(a).localeCompare(planFingerprint(b)));
  }
  value.workspace = payload.workspace ?? null;
  value.config = { ...DEFAULT_CONFIG, ...(payload.config as object ?? {}) };
  return planFingerprint(value);
}

export function baselinePlan(value: unknown): Record<string, unknown> | null {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    validateCloudPlan(parsed as Record<string, unknown>);
    return parsed as Record<string, unknown>;
  } catch { return null; }
}

export function decidePlanSync(local: Record<string, unknown>, remote: Record<string, unknown> | null,
  baseline: Record<string, unknown> | null, ownership: 'pristine' | 'owned' | 'unknown',
): 'equal' | 'upload' | 'restore' | 'paused' | 'removed' {
  const current = effectivePlanFingerprint(local);
  const account = remote ? effectivePlanFingerprint(remote) : null;
  const previous = baseline ? effectivePlanFingerprint(baseline) : null;
  if (account === current) return 'equal';
  if (previous && !remote) return 'removed';
  if (previous) {
    if (account === previous) return 'upload';
    if (current === previous) return 'restore';
    return 'paused';
  }
  if (ownership === 'pristine' && current === effectivePlanFingerprint({})) return remote ? 'restore' : 'upload';
  if (ownership === 'owned' && !remote) return 'upload';
  if (!remote && current === effectivePlanFingerprint({})) return 'upload';
  return 'paused';
}

const REQUIRED: Record<typeof PLAN_LISTS[number], Record<string, string>> = {
  picks: { userId: 'string', setId: 'string', dayId: 'string', addedAt: 'string' },
  ratings: { userId: 'string', artistId: 'string', score: 'number', source: 'string', confidence: 'number', updatedAt: 'string' },
  notes: { userId: 'string', artistId: 'string', body: 'string' },
  mustSee: { userId: 'string', artistId: 'string', rank: 'number', hardLock: 'boolean' },
  profiles: { userId: 'string', energyPref: 'number', crowdTolerance: 'number' },
  constraints: { id: 'string', userId: 'string', kind: 'string', label: 'string', active: 'boolean' },
  members: { workspaceId: 'string', userId: 'string', displayName: 'string', color: 'string', role: 'string', joinedAt: 'string' },
  ledger: { workspaceId: 'string', userId: 'string', cumulativeRegret: 'number', decayedRegret: 'number', tokensRemaining: 'number' },
  votes: { conflictId: 'string', userId: 'string', optionId: 'string', tokenSpent: 'boolean', castAt: 'string' },
};
const object = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const hasFields = (row: Record<string, unknown>, fields: Record<string, string>) => Object.entries(fields)
  .every(([key, type]) => typeof row[key] === type && (type !== 'number' || Number.isFinite(row[key])));

export function validateCloudPlan(payload: Record<string, unknown>): void {
  const reject = (key: string): never => { throw new Error(`The saved ${key} are unreadable. No choices were restored.`); };
  if (!object(payload)) reject('plan');
  for (const key of PLAN_LISTS) {
    const list = payload[key];
    if (list === undefined) continue;
    if (!Array.isArray(list)) reject(key);
    for (const row of list as unknown[]) {
      if (!object(row) || !hasFields(row, REQUIRED[key])) reject(key);
      const item = row as Record<string, unknown>;
      for (const field of ['startTs', 'endTs']) {
        if (item[field] !== undefined && (typeof item[field] !== 'number' || !Number.isFinite(item[field]))) reject(key);
      }
      if (key === 'profiles' && (!object(item.subgenreWeights) || !Array.isArray(item.alignedWith))) reject(key);
      // A blank answer is stored as an absent field; a present one is a string.
      if (key === 'profiles' && ['goalPriority', 'staminaCurve', 'alignmentIntent']
        .some((field) => item[field] !== undefined && typeof item[field] !== 'string')) reject(key);
      if (key === 'constraints' && !object(item.params)) reject(key);
      if (key === 'notes' && item.tags !== undefined && (!Array.isArray(item.tags) || item.tags.some(
        (tag) => !object(tag) || !hasFields(tag, { name: 'string', intensity: 'number', confirmed: 'boolean' }),
      ))) reject(key);
    }
  }
  if (payload.workspace != null) {
    const workspace = payload.workspace;
    if (!object(workspace) || !hasFields(workspace, { id: 'string', festivalId: 'string', name: 'string', joinCode: 'string', createdBy: 'string' }) ||
      !object(workspace.settings) || !hasFields(workspace.settings, { hardLockCap: 'number', tokenAllocation: 'number', tokensForceOutcome: 'boolean', scoreVisibility: 'string', groupObjective: 'string', voteWindowMinutes: 'number' })) reject('workspace');
  }
  const matches = (value: unknown, example: unknown): boolean => {
    if (typeof example === 'number') return typeof value === 'number' && Number.isFinite(value);
    if (Array.isArray(example)) return Array.isArray(value) && value.length === example.length && value.every((v, i) => matches(v, example[i]));
    if (object(example)) return object(value) && Object.entries(example).every(([k, v]) => matches(value[k], v));
    return typeof value === typeof example;
  };
  if (payload.config != null && (!object(payload.config) || Object.entries(payload.config).some(([k, v]) =>
    !(k in DEFAULT_CONFIG) || !matches(v, DEFAULT_CONFIG[k as keyof typeof DEFAULT_CONFIG]),
  ))) reject('settings');
}

export async function restoreReviewedPlan(store: LocalStore, payload: Record<string, unknown>, backup: Record<string, unknown>, expectedRevision = store.planRevision()): Promise<number> {
  validateCloudPlan(payload);
  await store.setMeta('beforeCloudRestore', backup);
  if (store.planRevision() !== expectedRevision) throw new Error('New local edits arrived. Nothing was restored.');
  let rows = 0;
  const writes: Promise<void>[] = [];
  for (const name of PLAN_LISTS) {
    const list = (payload[name] ?? []) as unknown[];
    writes.push(store.replaceAll(name, list));
    rows += list.length;
  }
  writes.push(store.replaceAll('workspaces', payload.workspace ? [payload.workspace] : []));
  writes.push(store.setMeta('configOverrides', payload.config ?? {}));
  await Promise.all(writes);
  return rows;
}
